import api from '../../../services/api';

/**
 * Login
 */
export const loginUser = async (email, password) => {
    return api.post('/rfp/auth/login', { email, password });
};

/**
 * Register / Sign up
 */
export const registerUser = async (formData) => {
    return api.post('/rfp/auth/register', formData);
};

/**
 * Logout
 */
export const logoutUser = async () => {
    return api.post('/rfp/auth/logout');
};

/**
 * Send forgot-password OTP
 * POST /rfp/auth/forgot-password  { email }
 * Emails a 6-digit OTP (valid for 5 minutes). The answer is the same whether or not the email is
 * registered; the screen waits 30 seconds (esend_after) before allowing another request.
 * 200: message string.  422: validation error.
 * (Read the answer with parseForgotPasswordResponse in ../utils/authResponse.)
 */
export const sendForgotPasswordOTP = async (email) => {
    return api.post('/rfp/auth/forgot-password', { email });
};

/**
 * Resend the forgot-password OTP
 * POST /rfp/auth/resend-otp  { email }
 * Sends a new OTP; the previous one stops working.
 * 200: message string.  422: validation error.
 * 429: asked again within 30 seconds; the body has `retry_after` (seconds left).
 */
export const resendOTP = async (email) => {
    return api.post('/rfp/auth/resend-otp', { email });
};

/**
 * Verify OTP
 * POST /rfp/auth/verify-otp  { email, otp }
 * 200: a reset token (valid 10 minutes) for the "set a new password" step.
 * 400: OTP wrong or expired (5 wrong tries cancel the OTP).  422: validation error.
 * (Read the answer with parseVerifyOtpResponse in ../utils/authResponse.)
 */
export const verifyOTP = async (email, otp) => {
    return api.post('/rfp/auth/verify-otp', { email, otp });
};
/**
 * Reset password
 */
export const resetPassword = async (resetToken, newPassword, confirmPassword) => {
    return api.post('/rfp/auth/reset-password', { 
        reset_token: resetToken, 
        new_password: newPassword, 
        confirm_password: confirmPassword 
    });
};

/**
 * Fetch the current user's team members
 */
export const fetchMyTeam = async () => {
    return api.get('/rfp/auth/my-team');
};

/**
 * Fetch all users with organizational hierarchy metadata
 * GET /rfp/auth/users
 */
export const fetchAllUsers = async (params = {}) => {
    return api.get('/rfp/auth/users', { params });
};

/**
 * Fetch all roles for dropdowns
 * GET /rfp/auth/roles
 */
export const fetchRoles = async () => {
    return api.get('/rfp/auth/roles');
};

/**
 * Permanently delete a user (Admin only)
 * DELETE /rfp/auth/users/{user_id}
 */
export const deleteUser = async (userId) => {
    return api.delete(`/rfp/auth/users/${userId}`);
};

/**
 * First-login "set new password" for users created by an admin.
 * POST /rfp/auth/set-password – needs the login token and only works while password_set is false.
 * `oldPassword` is the temporary password from the welcome email.
 * Responses: 200 (message string), 400 (wrong old password / same as old / already set), 422 (validation).
 *
 * The user is not "logged in" to the app yet at this point, so the token is passed explicitly
 * instead of being read from localStorage.
 */
export const setNewPassword = async ({ oldPassword, newPassword, confirmPassword, token }) => {
    return api.post(
        '/rfp/auth/set-password',
        {
            old_password: oldPassword,
            new_password: newPassword,
            confirm_password: confirmPassword,
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
    );
};